import { useState, useEffect } from 'react'
import { useSearchUsers } from '@/hooks/queries/useSearchUsers'
import { User } from '@/interfaces/user'
import { XIcon } from 'lucide-react'

interface UserSearchInputProps {
  selectedUser: User | null
  onSelectUser: (user: User) => void
  onClearUser: () => void
  disabled?: boolean
  className?: string
  placeholder?: string
}

export const UserSearchInput = ({
  selectedUser,
  onSelectUser,
  onClearUser,
  disabled = false,
  className = '',
  placeholder = 'Tìm kiếm...',
}: UserSearchInputProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const { data: searchResults, isLoading } = useSearchUsers(searchQuery)

  const handleSelectUser = (user: User) => {
    setSearchQuery('')
    setShowResults(false)
    onSelectUser(user)
  }

  const handleClearSelection = () => {
    setSearchQuery('')
    setShowResults(false)
    onClearUser()
  }

  // Reset when disabled changes (e.g., when form is reset)
  useEffect(() => {
    if (disabled) {
      setSearchQuery('')
      setShowResults(false)
    }
  }, [disabled])

  return (
    <div className={`relative w-full ${className}`}>
      {selectedUser ? (
        /* Selected User Display */
        <div className="w-full rounded-[16px] h-[80px] px-6 py-4 bg-white border-2 border-primary text-gray-900 text-base shadow-md flex items-center justify-between">
          <div className="flex flex-col">
            <div className="font-medium text-gray-900">{selectedUser.name}</div>
            <div className="text-sm text-gray-500">
              {selectedUser.role}
              {selectedUser.bu?.departmentName && ` • ${selectedUser.bu.departmentName}`}
            </div>
          </div>
          <button
            onClick={handleClearSelection}
            disabled={disabled}
            className="hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed p-2 transition-colors rounded-full"
            aria-label="Xóa lựa chọn"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      ) : (
        /* Search Input */
        <>
          <input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value
              setSearchQuery(value)
              if (value.trim()) {
                setShowResults(true)
              } else {
                setShowResults(false)
              }
            }}
            onFocus={() => {
              if (searchQuery.trim() && searchResults?.length) {
                setShowResults(true)
              }
            }}
            disabled={disabled}
            className="w-full rounded-[16px] h-[80px] px-6 py-4 bg-white border-2 border-gray-200 text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-md transition-all"
          />

          {/* Search Results Dropdown */}
          {showResults && searchResults && searchResults.length > 0 && (
            <div className="rounded-2xl max-h-60 animate-in fade-in slide-in-from-top-2 absolute left-0 z-50 w-full mt-2 overflow-y-auto duration-200 bg-white border-2 border-gray-200 shadow-lg">
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelectUser(user)}
                  disabled={disabled}
                  className="hover:bg-primary/10 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed focus:bg-primary/10 focus:outline-none w-full px-6 py-4 text-left transition-colors border-b border-gray-100"
                >
                  <div className="font-medium text-gray-900">{user.name}</div>
                  <div className="text-sm text-gray-500">
                    {user.role} {user?.bu?.departmentName && ` • ${user.bu.departmentName}`}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Loading indicator for search */}
          {isLoading && searchQuery.trim() && (
            <div className="rounded-2xl animate-in fade-in absolute left-0 z-50 w-full px-6 py-4 mt-2 text-center text-gray-500 duration-200 bg-white border-2 border-gray-200 shadow-lg">
              <div className="flex items-center justify-center gap-2">
                <div className="border-t-primary animate-spin w-4 h-4 border-2 border-gray-300 rounded-full" />
                Đang tìm kiếm...
              </div>
            </div>
          )}

          {/* No results */}
          {!isLoading &&
            searchQuery.trim() &&
            showResults &&
            searchResults &&
            searchResults.length === 0 && (
              <div className="rounded-2xl animate-in fade-in absolute left-0 z-50 w-full px-6 py-4 mt-2 text-center text-gray-500 duration-200 bg-white border-2 border-gray-200 shadow-lg">
                Không tìm thấy kết quả
              </div>
            )}
        </>
      )}
    </div>
  )
}
